import Color from 'color';

export const Colors = {
	black: '#000000',
	gray1: '#f8f9fc',
	gray2: '#eaecf4',
	gray3: '#dddfeb',
	gray4: '#d1d3e2',
	gray5: '#b7b9cc',
	gray6: '#858796',
	gray7: '#6e707e',
	blue: '#4e73df',
	indigo: '#6610f2',
	purple: '#6f42c1',
	pink: '#e83e8c',
	red: '#e74a3b',
	orange: '#fd7e14',
	yellow: '#f6c23e',
	green: '#1cc88a',
	teal: '#20c9a6',
	cyan: '#36b9cc',
	white: '#ffffff',
	gray: '#858796',
	grayDark: '#5a5c69',
	primary: '#4e73df',
	secondary: '#858796',
	success: '#1cc88a',
	info: '#36b9cc',
	warning: '#f6c23e',
	danger: '#e74a3b',
	light: '#f8f9fc',
	dark: '#5a5c69',
	Gray: '#f2f2f2',
	Yellow: '#fff5cf',
	Orange: '#feddc2',
	Red: '#fdf6f7',
	Green: '#aaf3d8',
	Blue: '#c2d1ff',
	darkYellow: '#816600',
	darkOrange: '#c05702',
	darkGreen: '#148a5f',
	darkRed: '#923636',
	darkBlue: '#42548a',
} as const;

export type IColors = typeof Colors;

type BGColors = (typeof Colors)['grayDark' | 'white' | 'darkYellow' | 'darkOrange' | 'darkGreen' | 'black'];
export type ColorValues = IColors[keyof IColors];
type ColorSwaps = {
	[key in ColorValues]: BGColors;
};

const ColorSwapMap: ColorSwaps = {
	[Colors.black]: Colors.white,
	[Colors.gray1]: Colors.grayDark,
	[Colors.gray2]: Colors.grayDark,
	[Colors.gray3]: Colors.grayDark,
	[Colors.gray4]: Colors.grayDark,
	[Colors.gray5]: Colors.grayDark,
	[Colors.gray6]: Colors.white,
	[Colors.gray7]: Colors.white,
	[Colors.blue]: Colors.white,
	[Colors.indigo]: Colors.white,
	[Colors.purple]: Colors.white,
	[Colors.pink]: Colors.white,
	[Colors.red]: Colors.white,
	[Colors.orange]: Colors.white,
	[Colors.yellow]: Colors.grayDark,
	[Colors.green]: Colors.white,
	[Colors.teal]: Colors.white,
	[Colors.cyan]: Colors.white,
	[Colors.white]: Colors.grayDark,
	[Colors.grayDark]: Colors.white,
	[Colors.Yellow]: Colors.darkYellow,
	[Colors.Orange]: Colors.darkOrange,
	[Colors.Green]: Colors.darkGreen,
	[Colors.Gray]: Colors.black,
	[Colors.Red]: Colors.white,
	[Colors.Blue]: Colors.white,
	[Colors.darkYellow]: Colors.white,
	[Colors.darkOrange]: Colors.white,
	[Colors.darkGreen]: Colors.white,
	[Colors.darkRed]: Colors.white,
	[Colors.darkBlue]: Colors.white,
};

export function fixedBgToTextColor(colorHex: ColorValues): BGColors {
	return ColorSwapMap[colorHex];
}

const light = new Color(Colors.white);
const dark = new Color(Colors.dark);

export function getContrastingTextColor(colorHex: string): string {
	const color = new Color(colorHex);

	const lightTextContrast = color.contrast(light);
	const darkTextContrast = color.contrast(dark);
	if (lightTextContrast > darkTextContrast) {
		return Colors.white;
	}

	return Colors.dark;
}

export function transparentize(colorValue: string, opacity: number) {
	const color = new Color(colorValue);
	return color.fade(opacity).rgb().string();
}

export function darken(colorValue: string, strength: number) {
	const color = new Color(colorValue);
	return color.darken(strength).rgb().string();
}

export function lighten(colorValue: string, strength: number) {
	const color = new Color(colorValue);
	return color.lighten(strength).rgb().string();
}

export interface ColorScheme {
	background: ColorValues;
	color: ColorValues;
}

type ColorSchemeKeys = 'Gray' | 'Yellow' | 'Orange' | 'Red' | 'Green' | 'Blue';
type IColorScheme = {
	[key in ColorSchemeKeys]: ColorScheme;
};

export const ColorSchemes: IColorScheme = {
	Gray: {
		background: '#f2f2f2',
		color: '#000000',
	},
	Yellow: {
		background: '#fff5cf',
		color: '#816600',
	},
	Orange: {
		background: '#feddc2',
		color: '#c05702',
	},
	Red: {
		background: '#fdf6f7',
		color: '#923636',
	},
	Green: {
		background: '#aaf3d8',
		color: '#148a5f',
	},
	Blue: {
		background: '#c2d1ff',
		color: '#42548a',
	},
};
